import { backbone, LocationHooks } from '@visikon/backbone';
import { Translation } from '@visikon/core-models/languageTypes';
import { tracker } from '@visikon/tracker/src';
import { MOBILE_SIZE, useWindowSizeMatches } from '@visikon/utils/src/responsive';
import * as React from 'react';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useNavLinkHelper } from '../../common/NavigationHelper';
import { useContent } from '../../common/selectors';
import { ContentContainerSection } from '../../components/ContentContainerSection';
import { AddButton } from '../../components/edit/AddButton';
import { WithFooter } from '../../components/Footer';
import { FlexWrapper, SmallViewSidePadding } from '../../components/styled/ContentAreaContainers';
import { DetailsCardContainer } from '../../components/styled/DetailsCard';
import { ScreenHeader, SubHeader } from '../../components/styled/Texts';
import { I18N } from '../../internationalization/AppText';
import { SymptomData, SymptomsType } from '../../reducers/resourcesReducer';
import { useSelfServiceUnsavedChanges } from '../embeddedMessageScript/EmbeddedMessageScript';
import { renderDetails, SymptomCardComponent } from './SymptomCardComponent';

const preserveSymptomProps = (apiContent: Translation<SymptomsType>, unsavedSymptoms: Translation<SymptomsType>) => {
  console.log('preserveSymptomProps', apiContent, unsavedSymptoms);
  if (!apiContent || !unsavedSymptoms) {
    return null;
  }

  const findThumbData = (id: string) => {
    return apiContent.data?.symptoms?.find((s) => s.thumb?._id === id)?.thumb || null;
  };

  unsavedSymptoms?.data?.symptoms?.map((symptom: any) => {
    if (symptom.thumb && symptom.thumb._idRef) {
      symptom.thumb = findThumbData(symptom.thumb._idRef) || symptom.thumb;
    }
  });

  return unsavedSymptoms;
};

export const SymptomsComponent: React.FC<PropsWithChildren & { fieldKey: string }> = ({ fieldKey, children: header }) => {
  const content = useContent<SymptomsType>(fieldKey);
  const isMobileView = useWindowSizeMatches(MOBILE_SIZE);
  const [detailView, setDetailView] = useState<SymptomData | undefined>(undefined);
  const [currentIndexView, setCurrentIndexView] = useState<number | undefined>(undefined);
  const languageCode = backbone.store.getState().country.languageCode;

  const unsavedChanges = useSelfServiceUnsavedChanges();

  const getClickHandler = (symptom: SymptomData, index: number) => {
    return () => {
      if (currentIndexView !== index) {
        tracker.trackScreen('SymptomDetails');
        tracker.trackEvent('Symptom', 'symptomShow', symptom.name);
        setDetailView(symptom);
        setCurrentIndexView(index);
      } else {
        tracker.trackScreen('Symptoms');
        setDetailView(undefined);
        setCurrentIndexView(undefined);
      }
    };
  };

  const operation = unsavedChanges.operations.find((op) => op.contentType == fieldKey);
  const unsavedSymptoms = preserveSymptomProps(
    content,
    operation?.data.translations.find((t) => t.language === languageCode),
  );

  const symptoms =
    unsavedSymptoms && !isMobileView
      ? unsavedSymptoms.data?.symptoms ??
        (() => {
          throw new Error('There should be symptoms?');
        })()
      : content?.data?.symptoms || [];

  const renderSymptom = (symptom: SymptomData, index: number) => {
    const isOpen = detailView ? index === currentIndexView : false;

    return (
      <SymptomCardComponent
        key={index}
        symptom={symptom}
        isOpen={isOpen}
        onClick={getClickHandler(symptom, index)}
        length={symptoms.length}
        currentPosition={index}
      />
    );
  };

  const symptomDetailView = detailView ? <DetailsCardContainer>{detailView.infoList?.map(renderDetails)}</DetailsCardContainer> : undefined;
  const contentView = symptomDetailView && isMobileView ? symptomDetailView : symptoms.map(renderSymptom);

  const subHeaderText = isMobileView && detailView ? '' : content?.data.description;

  const headerText = isMobileView && detailView ? <ScreenHeader>{detailView.name}</ScreenHeader> : header;

  const { isMobileOrHasNoMenu, contentNavLink, translatedPage, updateNavLink } = useNavLinkHelper();
  const level = LocationHooks.useNavLevel();

  const paths = LocationHooks.usePaths();
  const pathComingFrom = paths[paths.length - 1];

  useEffect(() => {
    if (detailView && isMobileOrHasNoMenu) {
      updateNavLink({
        title: translatedPage,
        path: pathComingFrom,
        action: () => {
          setDetailView(undefined);
          updateNavLink(contentNavLink);
        },
      });
    }
  }, [isMobileOrHasNoMenu, detailView, translatedPage, level]);

  return (
    <FlexWrapper>
      <ContentContainerSection style={{ display: 'block' }}>
        <SmallViewSidePadding>
          <WithFooter footerStyle={{ paddingTop: '16px' }}>
            {headerText}
            <SubHeader>{subHeaderText}</SubHeader>
            {!content ? (
              <SubHeader>
                <I18N text="loading_dotdotdot" />
                ...
              </SubHeader>
            ) : null}
            {content && contentView}
            {content && (
              <div style={{ marginTop: '8px' }}>
                <AddButton contentType="symptoms" />
              </div>
            )}
          </WithFooter>
        </SmallViewSidePadding>
      </ContentContainerSection>
    </FlexWrapper>
  );
};
